import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ShoppingCart, Plus, Minus, X, Package, ArrowRight, ShoppingBag } from 'lucide-react';
import { products } from '../data/mock';
import { availableAddons, addToCart, getCartCount } from '../data/cart';
import { useToast } from '../hooks/use-toast';
import { Checkbox } from '../components/ui/checkbox';

const Shop = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [quantities, setQuantities] = useState({});
  const [selectedAddons, setSelectedAddons] = useState({});
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    setCartCount(getCartCount());
  }, []);

  const handleQuantityChange = (productId, delta) => {
    setQuantities(prev => ({
      ...prev,
      [productId]: Math.max(1, (prev[productId] || 1) + delta)
    }));
  };

  const handleAddonToggle = (productId, addon) => {
    setSelectedAddons(prev => {
      const productAddons = prev[productId] || [];
      const exists = productAddons.find(a => a.id === addon.id);
      
      if (exists) {
        return {
          ...prev,
          [productId]: productAddons.filter(a => a.id !== addon.id)
        };
      } else {
        return {
          ...prev,
          [productId]: [...productAddons, addon]
        };
      }
    });
  };

  const handleAddToCart = (product) => {
    const quantity = quantities[product.id] || 1;
    const addons = selectedAddons[product.id] || [];
    
    addToCart(product, quantity, addons);
    
    setCartCount(getCartCount());
    
    toast({
      title: "Added to Cart!",
      description: `${product.name} (${quantity}x) added successfully.`,
    });

    // Reset selections
    setQuantities(prev => ({ ...prev, [product.id]: 1 }));
    setSelectedAddons(prev => ({ ...prev, [product.id]: [] }));
  };

  const handleBuyNow = (product) => {
    handleAddToCart(product);
    navigate('/cart');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-cyan-50">
      {/* Header Section */}
      <section className="bg-gradient-to-r from-cyan-600 to-teal-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <Badge className="bg-white text-cyan-600 mb-4">
                <ShoppingBag className="h-4 w-4 mr-2" />
                Online Store
              </Badge>
              <h1 className="text-5xl font-bold mb-6">Shop Smart RO Systems</h1>
              <p className="text-xl text-cyan-50 max-w-3xl">
                Purchase your Niloman HX Smart RO system online with professional installation and warranty
              </p>
            </div>
            <Button 
              onClick={() => navigate('/cart')}
              size="lg"
              className="bg-white text-cyan-600 hover:bg-gray-100 relative"
            >
              <ShoppingCart className="h-5 w-5 mr-2" />
              View Cart
              {cartCount > 0 && (
                <Badge className="absolute -top-2 -right-2 bg-orange-500 text-white border-0 h-6 w-6 rounded-full p-0 flex items-center justify-center">
                  {cartCount}
                </Badge>
              )}
            </Button>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {products.map((product, index) => (
              <Card 
                key={product.id}
                className="overflow-hidden border-2 shadow-xl hover:shadow-2xl transition-all duration-300"
              >
                <div className="grid lg:grid-cols-2 gap-8">
                  {/* Product Image */}
                  <div className="relative h-96 lg:h-auto">
                    <img 
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                    <Badge className="absolute top-4 right-4 bg-green-500 text-white border-0">
                      <Package className="h-3 w-3 mr-1" />
                      In Stock
                    </Badge>
                  </div>

                  {/* Product Details */}
                  <div className="p-8 flex flex-col">
                    <div className="flex-grow">
                      <Badge className="mb-3 bg-cyan-100 text-cyan-700 border-0">
                        {product.category}
                      </Badge>
                      <h2 className="text-3xl font-bold text-gray-900 mb-3">
                        {product.name}
                      </h2>
                      <p className="text-lg text-gray-600 mb-4">
                        {product.idealFor}
                      </p>

                      <div className="grid grid-cols-2 gap-4 mb-6">
                        <div>
                          <span className="text-sm text-gray-500">Capacity:</span>
                          <p className="font-semibold text-gray-900">{product.capacity}</p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-500">Purification:</span>
                          <p className="font-semibold text-gray-900">{product.stages.split('(')[0]}</p>
                        </div>
                      </div>

                      {/* Key Features */}
                      <div className="mb-6">
                        <h3 className="font-semibold text-gray-900 mb-3">Key Features:</h3>
                        <ul className="space-y-2">
                          {product.features.slice(0, 4).map((feature, idx) => (
                            <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
                              <span className="text-teal-600 mt-1">•</span>
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Add-ons */}
                      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                        <h3 className="font-semibold text-gray-900 mb-3">Optional Add-ons:</h3>
                        <div className="space-y-3">
                          {availableAddons.map(addon => (
                            <div key={addon.id} className="flex items-start gap-3">
                              <Checkbox
                                checked={(selectedAddons[product.id] || []).some(a => a.id === addon.id)}
                                onCheckedChange={() => handleAddonToggle(product.id, addon)}
                              />
                              <div className="flex-grow">
                                <div className="flex items-center justify-between">
                                  <p className="text-sm font-medium text-gray-900">{addon.name}</p>
                                  <span className="text-sm font-semibold text-gray-900">+₹{addon.price.toLocaleString()}</span>
                                </div>
                                <p className="text-xs text-gray-600">{addon.description}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Quantity Selector */}
                      <div className="mb-6">
                        <label className="text-sm font-medium text-gray-700 mb-2 block">Quantity:</label>
                        <div className="flex items-center gap-3">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleQuantityChange(product.id, -1)}
                            disabled={(quantities[product.id] || 1) <= 1}
                          >
                            <Minus className="h-4 w-4" />
                          </Button>
                          <span className="text-xl font-semibold w-12 text-center">
                            {quantities[product.id] || 1}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleQuantityChange(product.id, 1)}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Price and Actions */}
                    <div className="border-t pt-6">
                      <div className="flex items-baseline justify-between mb-4">
                        <div>
                          <span className="text-3xl font-bold text-gray-900">{product.price}</span>
                          <span className="text-sm text-gray-500 ml-2">+ taxes</span>
                        </div>
                        {(selectedAddons[product.id] || []).length > 0 && (
                          <div className="text-right">
                            <p className="text-sm text-gray-600">Add-ons:</p>
                            <p className="text-lg font-semibold text-teal-600">
                              +₹{(selectedAddons[product.id] || []).reduce((sum, a) => sum + a.price, 0).toLocaleString()}
                            </p>
                          </div>
                        )}
                      </div>

                      <div className="flex gap-3">
                        <Button
                          onClick={() => handleAddToCart(product)}
                          variant="outline"
                          className="flex-1 border-2 border-cyan-500 text-cyan-600 hover:bg-cyan-50"
                        >
                          <ShoppingCart className="h-4 w-4 mr-2" />
                          Add to Cart
                        </Button>
                        <Button
                          onClick={() => handleBuyNow(product)}
                          className="flex-1 bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white"
                        >
                          Buy Now
                          <ArrowRight className="h-4 w-4 ml-2" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold text-cyan-600 mb-2">Free Delivery</div>
              <p className="text-gray-600">On all prepaid orders</p>
            </div>
            <div>
              <div className="text-3xl font-bold text-teal-600 mb-2">Easy Returns</div>
              <p className="text-gray-600">7-day return policy</p>
            </div>
            <div>
              <div className="text-3xl font-bold text-cyan-600 mb-2">Secure Payment</div>
              <p className="text-gray-600">100% safe & secure</p>
            </div>
            <div>
              <div className="text-3xl font-bold text-teal-600 mb-2">Warranty</div>
              <p className="text-gray-600">1-3 years coverage</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Shop;
